import React, { useState, useRef, useEffect } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    photographyType: '',
    eventDate: '',
    message: '',
  });
  const [mailtoLink, setMailtoLink] = useState('');
  const mailtoRef = useRef(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const subject = `New Photography Inquiry - ${formData.photographyType || 'General'}`;
    const body = `Hello,\n\nYou have received a new photography inquiry from your website.\n\nCLIENT DETAILS:\n--------------\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nSERVICE DETAILS:\n---------------\nPhotography Type: ${formData.photographyType}\nEvent Date: ${formData.eventDate || 'Not specified'}\n\nMESSAGE:\n--------\n${formData.message}\n\n---\nThis inquiry was submitted through the contact form on jessecamarena.com`;

    const link = `mailto:jessecamarena008@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setMailtoLink(link);
  };

  // Auto-click the mailto link when it's set
  useEffect(() => {
    if (mailtoLink && mailtoRef.current) {
      mailtoRef.current.click();
      // Reset form after clicking
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          photographyType: '',
          eventDate: '',
          message: '',
        });
        setMailtoLink('');
      }, 1000);
    }
  }, [mailtoLink]);

  return (
    <div className="pt-20 md:pt-24 min-h-screen bg-light">
      {/* Hidden mailto link that gets auto-clicked */}
      {mailtoLink && (
        <a
          ref={mailtoRef}
          href={mailtoLink}
          style={{ display: 'none' }}
          aria-hidden="true"
        >
          Email Link
        </a>
      )}

      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-forest via-secondary to-forest text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-accent rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-white rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="font-serif text-4xl md:text-6xl font-bold mb-6">
            Let's Work Together
          </h1>
          <p className="text-xl text-gray-200">
            Ready to capture your special moments? Get in touch for a free quote
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl">
              <h2 className="font-serif text-3xl font-bold text-forest mb-8">
                Send a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-forest mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="w-full px-4 py-3 rounded-xl border-2 border-card focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition-all duration-300"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-forest mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 rounded-xl border-2 border-card focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition-all duration-300"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-forest mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="(555) 123-4567"
                    className="w-full px-4 py-3 rounded-xl border-2 border-card focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition-all duration-300"
                  />
                </div>

                <div>
                  <label htmlFor="photographyType" className="block text-sm font-semibold text-forest mb-2">
                    Type of Photography *
                  </label>
                  <select
                    id="photographyType"
                    name="photographyType"
                    value={formData.photographyType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-card focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition-all duration-300"
                  >
                    <option value="">Select a service</option>
                    <option value="Wedding Photography">Wedding Photography</option>
                    <option value="Event Photography">Event Photography</option>
                    <option value="Portrait Photography">Portrait Photography</option>
                    <option value="Engagement Photography">Engagement Photography</option>
                    <option value="Graduation Photography">Graduation Photography</option>
                    <option value="Live Performance Photography">Live Performance Photography</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="eventDate" className="block text-sm font-semibold text-forest mb-2">
                    Event Date
                  </label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-card focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition-all duration-300"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-forest mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="Tell us about your photography needs..."
                    className="w-full px-4 py-3 rounded-xl border-2 border-card focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition-all duration-300 resize-none"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-forest hover:bg-secondary text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Send Message
                </button>

                <p className="text-sm text-secondary text-center mt-4">
                  * Required fields
                </p>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="font-serif text-3xl font-bold text-forest mb-8">
                  Other Ways to Reach Out
                </h2>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-card/30">
                <div className="text-forest mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="font-serif text-2xl font-semibold text-forest mb-3">
                  Email Directly
                </h3>
                <p className="text-secondary mb-4">
                  Prefer to email? Reach out directly at:
                </p>
                <a 
                  href="mailto:jessecamarena008@gmail.com" 
                  className="text-forest hover:text-secondary font-semibold transition-smooth text-lg break-all"
                >
                  jessecamarena008@gmail.com
                </a>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-card/30">
                <div className="text-forest mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-serif text-2xl font-semibold text-forest mb-3">
                  Response Time
                </h3>
                <p className="text-secondary">
                  We typically respond within 24 hours. Looking forward to hearing from you!
                </p>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-card/30">
                <div className="text-forest mb-4">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-serif text-2xl font-semibold text-forest mb-3">
                  Free Quote
                </h3>
                <p className="text-secondary">
                  Every inquiry receives a personalized free quote tailored to your needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
